import React from "react";
import Image from "@/components/Atoms/Image";
import "react-multi-carousel/lib/styles.css";
import { OverlayShadow } from "@/components/Molecules/OverlayShadow/Overlay";
import { FullBackgroundImageHero } from "@/components/Molecules/ImageCard";
import { FeaturedCopyCard } from "@/components/Molecules/HeroCopyCard";
import { HubSpotForm } from "@/components/Molecules/Forms";
import { sliderImageBgColorSelection } from "@/lib/fragments";
import {
  resolvePortfolioBackgroundHex,
  resolvePortfolioBackgroundStyle,
} from "@/lib/resolve-background-color";
import HeroFeaturedGridDesktop from "./HeroFeaturedGridDesktop";
import HeroFeaturedGridMobile from "./HeroFeaturedGridMobile";
import HeroFeaturedZoomImage from "./HeroFeaturedZoomImage";

export default function HeroFeatured(data: any[any]) {
  const showZoom = Boolean(data.addZoom && data.featuredImage);
  const isReducedLayout =
    showZoom ||
    data.layout === "reduced" ||
    data.layout === "Reduced";
  const isGridLayout =
    isReducedLayout ||
    data.layout === "grid" ||
    data.layout === "Grid";
  const isContain = data.imagePosition === "Contain";
  const backgroundStyle = resolvePortfolioBackgroundStyle(
    data.bgColorGr,
    data.bgColor
  );
  const backgroundHex =
    resolvePortfolioBackgroundHex(data.bgColorGr, data.bgColor) ?? "#ffffff";
  const zoomIconColor = data.textColor?.color;

  const copyCard = (
    <FeaturedCopyCard
      parallaxImage={[data?.addParallaxElements, data?.parallaxAsset]}
      label={[data.addLabel, data.label]}
      title={data.title}
      description={data.description}
      color={data.textColor?.color}
      descriptionColor={data.descriptionColor?.color}
      cta={[false, []]}
      hasSpacer={data.useSpacer}
      layout={data.textAlignment}
      embedded={isGridLayout}
      underline={[
        data.addTitleUnderline,
        data.customUnderlineAsset,
        data.underlineColor,
      ]}
    />
  );

  const overlay = data.overlay ? (
    <OverlayShadow
      format={data.textAlignment}
      data={data.overlay}
      color={data.imageOverlayBaseColor}
      layout={data.textAlignment}
    />
  ) : null;

  const backgroundHero =
    !data.swapBgForColor && data.backgroundImage ? (
      <FullBackgroundImageHero
        position={data.imagePosition}
        data={data.backgroundImage}
      />
    ) : null;

  if (isGridLayout) {
    const imageColData = data.featuredImage ?? data.backgroundImage;
    const formColumn =
      data.addContent &&
      (
        <div
          style={backgroundStyle}
          className="relative z-10 col-span-1 flex flex-col justify-center py-12 text-center md:pt-[120px]"
        >
          <HubSpotForm data={data?.contentType} />
        </div>
      );

    const gridProps = {
      backgroundStyle,
      isContain,
      overlay,
      backgroundHero,
      copyCard,
      imageColData,
      featuredImage: data.featuredImage,
      formColumn,
      textAlignment: data.textAlignment,
      addZoom: data.addZoom,
      zoomIconColor,
      isReducedLayout,
    };

    return (
      <>
        <div className="lg:hidden">
          <HeroFeaturedGridMobile
            id={data.id}
            backgroundHex={backgroundHex}
            {...gridProps}
          />
        </div>
        <div className="hidden lg:block">
          <HeroFeaturedGridDesktop id={data.id} {...gridProps} />
        </div>
      </>
    );
  }

  const classicBackgroundLayer =
    showZoom && backgroundHero ? (
      <div className="absolute top-0 h-[40vh] w-full lg:h-[85%]">
        {overlay}
        {backgroundHero}
      </div>
    ) : (
      <>
        {overlay}
        {backgroundHero}
      </>
    );

  return (
    <div
      className={`relative overflow-hidden ${
        showZoom ? "h-[90vh] max-h-[750px] pt-16 md:pt-28" : "h-[90vh] max-h-[750px]"
      }`}
      id={data.id}
      style={backgroundStyle}
    >
      <div
        className={`grid ${
          data.addContent ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
        }`}
      >
        <div className="relative col-span-1">
          {classicBackgroundLayer}

          <div
            className="relative mx-auto grid h-full w-full grid-cols-1 gap-6 px-6 md:gap-0"
          >
            <div
              className={`col-span-1 flex items-center ${
                !data.addContent
                  ? "m-auto max-w-[90%] pt-[150px]"
                  : "pt-[100px] lg:translate-y-[10rem] lg:pt-[0px]"
              }`}
            >
              {copyCard}
            </div>
            <div
              className={`col-span-1 bottom-[-4rem] left-0 right-0 flex items-end ${
                !data.addContent
                  ? "translate-y-[5rem]"
                  : "translate-y-[2rem] md:translate-y-[4rem]"
              }`}
            >
        {showZoom ? (
          <HeroFeaturedZoomImage
            image={data.featuredImage}
            iconColor={zoomIconColor}
            imageClassName="mx-auto max-w-[15rem] sm:max-w-[18rem] lg:max-w-[28rem]"
            buttonClassName="right-[5rem] md:right-[25%]"
          />
        ) : (
          <div className="mx-auto flex h-[min(55dvh,28rem)] w-full max-w-[400px] items-end justify-center">
            <Image
              data={data?.featuredImage}
              className="max-h-full max-w-full object-contain object-bottom"
              objectFit="contain"
            />
          </div>
        )}
            </div>
          </div>
        </div>
        {data.addContent && (
          <div
            style={backgroundStyle}
            className="relative z-10 col-span-1 flex flex-col justify-center py-12 text-center md:pt-[120px]"
          >
            <HubSpotForm data={data?.contentType} />
          </div>
        )}
      </div>
    </div>
  );
}
export const fragment = `
    fragment HeroFeaturedFragment on SectionHeroFeaturedRecord{
      __typename
      id
      name
      title
      description
      layout
      addLabel
      label
      imagePosition
      textColor{
        color{
          hex
        }
      }
      descriptionColor {
        color {
          hex
        }
      }
        overlay
        imageOverlayBaseColor {
          hex
        }
        textAlignment
        textFlexPosition
        bgColor{
          color{
            hex
            cssRgb
          }
        }
        bgColorGr {
          ${sliderImageBgColorSelection}
        }
        swapBgForColor
        addZoom
        addScrollIcon
        scrollIconType
        iconColor {
          color {
            hex
            alpha
          }
        }
        iconBackgroundColor {
          color {
            hex
          }
        }
        addTitleUnderline
        underlineColor{
          color{
            hex
          }
        }
        customUnderlineAsset{
          img{
            url
            width
            height
          }
        }
        addCustomIconBackgroundElement
        iconBackgroundAsset {
          url
          width
          height
        }
        customScrollIcon {
          url
          width
          height
        }
        useSpacer
        addParallaxElements
        parallaxAsset {
          url
        }
      featuredImage {
        id
        title
        alt
        tags
        format
        focalPoint {
          y
          x
        }
        responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
          ... responsiveImageFragment
        }
      }
      backgroundImage {
        id
        title
        alt
        tags
        format
        focalPoint {
          y
          x
        }
        responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
          ... responsiveImageFragment
        }
      }
      addContent
      contentType {
        __typename
        ...FormFragment
      }
    }
`;
